import { DUNGEON, SKILLS } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { hexDist, hexToWorld, worldToHex } from '../hex.ts';
import { cosA, dist2, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import { enemiesNear, targetable } from '../battle/combat.ts';
import { dangerAt, standing } from '../battle/dungeon.ts';
import { puzzleOf } from '../battle/deep.ts';
import { onSegment } from '../battle/sima.ts';
import { SIMA_GUARDIAN_KINDS } from '../battle/sima-bosses.ts';
import { shining } from '../battle/obsidian.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { clericCare, useSkills } from './ghost.ts';

/** A spot this far from danger counts as safe for a few ticks of walking. */
const DODGE_LOOKAHEAD = 20;
/** A hero whose blow reaches this far or less fights in melee. */
const MELEE_REACH = 1500;

/** The Warden's marks, and in the crypt in embers its lava and the eruptions about to burst. */
const hot = (s: BattleState, x: number, y: number, ticks = 0x7fffffff, lava = false): boolean =>
  dangerAt(s, x, y, ticks) ||
  s.zones.some(
    (z) =>
      ((lava && z.kind === 'lava') || z.kind === 'glass' || z.kind === 'shards' || (z.kind === 'eruption' && (z.warn ?? -1) > 0 && (z.warn ?? 0) <= ticks + 12)) &&
      withinRange(x, y, z.x, z.y, z.radius + 200),
  ) ||
  simaHot(s, x, y, ticks);

/** The Chasm's marked strikes about to land (a beam's whole line), and the fires and ink that stay. */
const simaHot = (s: BattleState, x: number, y: number, ticks: number): boolean =>
  !!s.dungeon?.sima &&
  (s.zones.some((z) => {
    if (z.kind !== 'sima') return false;
    const marked = (z.warn ?? -1) > 0 && (z.warn ?? 0) <= ticks + 12;
    if (z.haz === 'beam') return marked && onSegment(x, y, z.x, z.y, z.tx ?? z.x, z.ty ?? z.y, z.radius + 200);
    // Low fires and ink are walked through to reach a foe (fleeing them, a bot never struck); spores put it to sleep.
    if (!marked && z.haz !== 'spores') return false;
    return withinRange(x, y, z.x, z.y, z.radius + 200);
  }) ||
    // The spore mother about to burst: away from her.
    actHot(s.dungeon.sima.act, x, y));

const actHot = (a: { kind: string; x: number; y: number; r: number } | null, x: number, y: number): boolean => !!a && a.kind === 'swell' && withinRange(x, y, a.x, a.y, a.r + 300);

const go = (s: BattleState, h: Hero, p: { x: number; y: number }, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, p.x, p.y, 200)) return;
  if (!h.moving || Math.abs(h.destX - p.x) + Math.abs(h.destY - p.y) > slack) orderMove(s, h, p.x, p.y);
};

/**
 * The deep crypt's puzzles, as a capable group plays them: bowls and levers shared out by hero order, one
 * hero walking the runes, everyone walking the glass bridge tile by tile. True while the hero is on it.
 */
const puzzleDuty = (s: BattleState, h: Hero): boolean => {
  const d = s.dungeon;
  if (!d || d.room < 0) return false;
  const p = puzzleOf(d, d.room);
  if (!p || p.solved) return false;
  const up = s.heroes.filter(standing);
  const me = up.indexOf(h);
  if (me < 0) return false;
  const at = (i: number): { x: number; y: number } => hexToWorld((p.spots[i] as { q: number }).q, (p.spots[i] as { r: number }).r);
  if (p.kind === 'braziers') {
    // My bowls: every n-th one; the first that is cold or about to go out.
    const mine = p.spots.map((_, i) => i).filter((i) => i % up.length === me);
    const due = mine.find((i) => (p.lit[i] ?? 0) <= s.tick + 36);
    if (due === undefined) return false;
    go(s, h, at(due));
    if (withinRange(h.x, h.y, at(due).x, at(due).y, 300)) {
      h.moving = false;
      h.path = [];
    }
    return true;
  }
  if (p.kind === 'levers') {
    if (me > 1) return false;
    const i = p.step * 2 + me;
    go(s, h, at(i));
    if (withinRange(h.x, h.y, at(i).x, at(i).y, 300)) {
      h.moving = false;
      h.path = [];
    }
    return true;
  }
  if (p.kind === 'runes') {
    if (me !== 0 || s.tick < p.showUntil) return false;
    go(s, h, at(p.lit[p.step] as number), 200);
    return true;
  }
  // Glass: from my tile to the next one along the path.
  const hx = worldToHex(h.x, h.y);
  const k = p.spots.findIndex((x) => x.q === hx.q && x.r === hx.r);
  if (k < 0) {
    go(s, h, at(0), 200);
    return true;
  }
  if (k >= p.spots.length - 1) {
    h.moving = false;
    h.path = [];
    return true;
  }
  if (s.tick < p.showFrom) return true;
  const nxt = at(k + 1);
  if (!h.moving || Math.abs(h.destX - nxt.x) + Math.abs(h.destY - nxt.y) > 100) {
    h.destX = nxt.x;
    h.destY = nxt.y;
    h.path = [];
    h.moving = true;
  }
  return true;
};

/** The rune seal, once its order has shown: the plate to step on next (the bot remembers the order). */
const sealPlate = (s: BattleState): { x: number; y: number } | null => {
  const seal = s.dungeon?.seal;
  const L = s.map.dungeon;
  if (!seal || seal.solved || !L || !s.dungeon?.cleared[L.rooms.length - 2] || seal.showUntil === 0 || s.tick < seal.showUntil) return null;
  const p = seal.plates[seal.order[seal.step] as number];
  return p ? hexToWorld(p.q, p.r) : null;
};

/**
 * A capable crypt player for headless balance runs (heroes with control 'merc'): steps out of
 * warnings and ink, picks fallen allies up, focuses the Warden, uses every skill including ultimates.
 * It also plays the spectres of absent friends brought along (`rules.spectres`), who follow the live heroes instead of
 * walking on. A dropped player's hero in the crypt is never driven by this: it just stands still.
 */
export const dungeonBot = (s: BattleState, h: Hero): void => {
  if (!standing(h) || h.stun > 0) return;
  // 1) Out of the warning, now.
  // (Lava only when standing on it: a path across a crack is walked through, not turned back from.)
  if (hot(s, h.x, h.y, DODGE_LOOKAHEAD, !h.moving)) {
    const safe = safeSpot(s, h);
    // Re-ordered only for a new spot: every new order restarts the walk from the cell's centre.
    if (safe && (!h.moving || Math.abs(h.destX - safe.x) + Math.abs(h.destY - safe.y) > 600)) orderMove(s, h, safe.x, safe.y);
    h.target = 0;
    return;
  }
  if (h.moving && hot(s, h.destX, h.destY)) h.moving = false;
  // A cleric with dawn ready raises the fallen from where it stands.
  if (h.cls === 'cleric' && h.skillCd[2] === 0 && s.heroes.some((o) => o.downTicks > 0 && withinRange(h.x, h.y, o.x, o.y, SKILLS.dawn.radius))) {
    castSkill(s, h, { t: 'skill', slot: 2 });
  }
  // 2) A fallen ally nearby and nothing on top of them: go and pick them up.
  const fallen = s.heroes.find((o) => o.downTicks > 0 && closestStanding(s, o) === h);
  if (fallen && h.hp * 10 > h.stats.maxHp * 3 && !hot(s, fallen.x, fallen.y)) {
    if (!withinRange(h.x, h.y, fallen.x, fallen.y, DUNGEON.reviveRange - 300)) {
      if (!h.moving || s.tick % 12 === 0) orderMove(s, h, fallen.x, fallen.y);
    } else {
      h.moving = false;
      h.path = [];
    }
    return;
  }
  // 2b) The deep crypt's puzzle comes before the fight.
  if (puzzleDuty(s, h)) return;
  // 3) Fight: the boss first, else the nearest nightmare (melee) or the weakest in range (ranged).
  const all = enemiesNear(s, h.x, h.y, 14000, (e) => targetable(s, e) && e.kind !== 'boneWall');
  if (!all.length) {
    if (h.cls === 'cleric') clericCare(s, h);
    const plate = sealPlate(s);
    if (plate) {
      if (!h.moving || Math.abs(h.destX - plate.x) + Math.abs(h.destY - plate.y) > 300) orderMove(s, h, plate.x, plate.y);
      return;
    }
    walkOn(s, h);
    return;
  }
  const boss = all.find((e) => e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus' || SIMA_GUARDIAN_KINDS.has(e.kind));
  const adds = all.filter((e) => e !== boss);
  // Adds near the healers first; the warrior keeps the boss busy.
  const target = h.cls === 'warrior' ? (boss ?? nearest(h, adds)) : (nearest(h, adds.filter((e) => withinRange(h.x, h.y, e.x, e.y, h.stats.range))) ?? boss ?? nearest(h, adds));
  if (!target) return;
  // A shining colossus turns skills back too: wait it out.
  if (shining(s, target)) {
    h.target = 0;
    return;
  }
  useSkills(s, h, target);
  if (h.cls !== 'cleric' && h.skillCd[2] === 0 && (boss || enemiesNear(s, target.x, target.y, 2500).length >= 3)) castSkill(s, h, { t: 'skill', slot: 2, x: target.x, y: target.y, id: target.id });
  // Ranged heroes back off from anything in their face (a hero that fights in melee, or a druid as a bear, stays on it).
  if (h.stats.range > MELEE_REACH && !((h.bear ?? 0) > 0)) {
    const close = adds.find((e) => withinRange(h.x, h.y, e.x, e.y, 900)) ?? (boss && withinRange(h.x, h.y, boss.x, boss.y, 1600) ? boss : undefined);
    if (close) {
      const away = { x: h.x + (h.x - close.x) * 2, y: h.y + (h.y - close.y) * 2 };
      const c = cellAtWorld(s, away.x, away.y);
      if (heroWalkable(s, c) && !hot(s, away.x, away.y)) {
        if (!h.moving || s.tick % 6 === 0) orderMove(s, h, away.x, away.y);
        h.target = 0;
        return;
      }
    }
  }
  h.target = target.id;
  h.forced = true;
};

const nearest = (h: Hero, list: Enemy[]): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(h.x, h.y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const closestStanding = (s: BattleState, o: Hero): Hero | null => {
  let best: Hero | null = null;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (!standing(h)) continue;
    const d = dist2(h.x, h.y, o.x, o.y);
    if (d < bd) {
      bd = d;
      best = h;
    }
  }
  return best;
};

/** The nearest open floor that no warning covers: around the hero, then around the Warden. */
const safeSpot = (s: BattleState, h: Hero): { x: number; y: number } | null => {
  const boss = s.enemies.find((e) => e.kind === 'warden' && e.hp > 0);
  const me = worldToHex(h.x, h.y);
  const home = s.map.dungeon?.rooms.find((r) => hexDist(me, r) <= r.radius);
  const centers: [number, number, number][] = [
    [h.x, h.y, 1700],
    [h.x, h.y, 2800],
    ...(boss ? ([[boss.x, boss.y, 1100]] as [number, number, number][]) : []),
  ];
  let best: { x: number; y: number } | null = null;
  let bd = 0x7fffffff;
  for (const [cx, cy, r] of centers) {
    for (let a = 0; a < 1024; a += 64) {
      const x = cx + mulBp(r, cosA(a));
      const y = cy + mulBp(r, sinA(a));
      // Only a closer spot can win: test the cheap distance before the floor and the dangers.
      const d = dist2(h.x, h.y, x, y);
      if (d >= bd) continue;
      // Inside the hero's own room (never out into a corridor, where a door may shut behind it).
      if (!heroWalkable(s, cellAtWorld(s, x, y)) || (home && hexDist(worldToHex(x, y), home) > home.radius - 1) || hot(s, x, y, 0x7fffffff, true)) continue;
      bd = d;
      best = { x, y };
    }
  }
  return best;
};

/** Nothing to fight: head for the next room that is not cleared yet (the doors decide how far). */
/** A spectre in the crypt: an absent friend's hero, brought along by whoever plays (docs/NIVELES.md). */
export const isSpectre = (s: BattleState, h: Hero): boolean => h.control === 'ghost' && !!s.rules?.spectres?.includes(h.pid);

/**
 * A spectre between fights keeps by the first hero standing who is played (live, or a balance bot in its player's
 * place): the players open the doors and choose the stairs, it never walks on ahead. With nobody playing (all dropped
 * or down) it holds where it is.
 */
const follow = (s: BattleState, h: Hero): void => {
  const lead = s.heroes.find((o) => o.control !== 'ghost' && standing(o));
  if (!lead || withinRange(h.x, h.y, lead.x, lead.y, FOLLOW_NEAR)) return;
  go(s, h, { x: lead.x + (h.post - 1) * 300, y: lead.y + 300 }, FOLLOW_NEAR);
};

/** A spectre this close to its lead stays put. */
const FOLLOW_NEAR = 1500;

const walkOn = (s: BattleState, h: Hero): void => {
  const d = s.dungeon;
  const L = s.map.dungeon;
  if (!d || !L) return;
  if (isSpectre(s, h)) return follow(s, h);
  // The deep crypt: down the stairs (balance runs always go as deep as they can).
  const dp = d.deep ?? d.sima;
  if (dp?.stairs) {
    const st = hexToWorld(dp.stairs.q, dp.stairs.r);
    go(s, h, { x: st.x + (h.post - 1) * 250, y: st.y });
    return;
  }
  // Catch your breath before the next door: nobody walks in hurt.
  if (s.heroes.some((o) => standing(o) && o.hp * 4 < o.stats.maxHp * 3)) return;
  const next = d.cleared.findIndex((c, i) => !c && i > 0);
  const room = L.rooms[next < 0 ? L.rooms.length - 1 : next];
  if (!room) return;
  const c = hexToWorld(room.q, room.r);
  if (!h.moving || s.tick % 36 === 0) orderMove(s, h, c.x + (h.post - 1) * 600, c.y);
};
